import { LocationDefinition } from "@/data/core";
import { CALENDAR_DATE_REGEX, getUtcMidnightTimestamp, parseYmd } from "./date";

const STARS_FORMATTER = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
  // Truncate like GitHub's own star display: 3,650 shows as 3.6K, never a
  // rounded-up 3.7K that would overstate the count. The >= 950K branch
  // pre-rounds to whole 100K steps, so truncation never alters it.
  roundingMode: "trunc",
});

const STARS_FORMATTER_NO_FRACTION = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 0,
});

function normalizeDateInput(
  dateValue?: string | number | Date | null,
): number | Date | null {
  if (dateValue === null || dateValue === undefined || dateValue === "")
    return null;

  if (typeof dateValue === "number") {
    return Number.isNaN(dateValue) ? null : dateValue;
  }

  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }

  // String input

  // Strict calendar date
  if (CALENDAR_DATE_REGEX.test(dateValue)) {
    try {
      return getUtcMidnightTimestamp(dateValue);
    } catch {
      return null;
    }
  }

  // Instant in time (must include timezone)
  const isInstant =
    dateValue.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(dateValue);

  if (!isInstant) {
    // Reject timezone-less timestamps like "YYYY-MM-DDT00:00:00"
    return null;
  }

  // Validate that the date part is a valid calendar date
  // (e.g. reject 2023-02-30 even if Date() would rollover)
  const datePart = dateValue.substring(0, 10);
  if (CALENDAR_DATE_REGEX.test(datePart)) {
    try {
      parseYmd(datePart);
    } catch {
      return null;
    }
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return parsedDate;
}

/**
 * Creates a date formatter function using the provided options.
 *
 * The returned function accepts:
 * - "YYYY-MM-DD" calendar dates: interpreted as UTC midnight for timezone-invariant display.
 * - ISO instants with timezone (ending in "Z" or containing "+HH:MM"/"-HH:MM"): parsed as an instant.
 * - Numbers and Date objects: formatted directly.
 *
 * Any other string format is treated as invalid and returns "".
 */
function createDateFormatter(options: Intl.DateTimeFormatOptions) {
  // Reusable Intl.DateTimeFormat instance to avoid expensive instantiation
  // during render cycles. TimeZone is explicitly set to UTC to keep calendar-date
  // rendering invariant across client timezones.
  const formatter = new Intl.DateTimeFormat("en-US", {
    ...options,
    timeZone: "UTC",
  });

  return (dateValue?: string | number | Date | null) => {
    const date = normalizeDateInput(dateValue);
    if (date === null) return "";
    return formatter.format(date);
  };
}

export const formatDateMedium = createDateFormatter({ dateStyle: "medium" });

export const formatDateMonthDay = createDateFormatter({
  month: "long",
  day: "numeric",
});

export const formatDateMonthShort = createDateFormatter({ month: "short" });

export const formatDateDay = createDateFormatter({ day: "numeric" });

export const formatDateLong = createDateFormatter({
  year: "numeric",
  month: "long",
  day: "numeric",
});

export const formatLocation = (
  location: LocationDefinition | null | undefined,
): string | null => (location ? `${location.city}, ${location.country}` : null);

/**
 * Formats a large number with K/M suffix using smart rounding:
 * - Below 10K: Show with one decimal if needed (e.g., 9.5K, 9K)
 * - 10K to under 1M: Round to nearest 1000 (e.g., 234,567 → 235K)
 * - 1M+: Round to nearest 100K (0.1M), whole millions shown without decimal (e.g., 1M, 2M)
 */
export function formatStars(stars: number): string {
  if (stars >= 950000) {
    // Round to nearest 100,000 (tenth of a million)
    return STARS_FORMATTER.format(Math.round(stars / 100000) * 100000);
  }
  if (stars >= 10000) {
    // Round to nearest 1000
    return STARS_FORMATTER_NO_FRACTION.format(stars);
  }
  // Below 10K: show with one decimal if needed
  return STARS_FORMATTER.format(stars);
}

/**
 * Formats an array of years into a readable string
 * e.g., [2019, 2020, 2021] -> "2019–2021"
 * e.g., [2018, 2020] -> "2018, 2020"
 *
 * Contract:
 * - Input should be an array of integers representing years.
 * - Non-integer values (floats, NaN) are treated as list entries and never collapsed into a range.
 * - The O(1) consecutive check is only used when all deduplicated values are integers.
 */
export function formatYears(years: number[]): string {
  if (years.length === 0) return "";

  const sorted = Array.from(new Set(years))
    .filter(Number.isFinite)
    .sort((a, b) => a - b);

  if (sorted.length === 0) return "";
  if (sorted.length === 1) return sorted[0]!.toString();

  // Check if consecutive
  // Since the array is sorted and unique, we can just check if the range matches the length
  // Note: This logic assumes all inputs are integers.
  const isConsecutive =
    sorted[sorted.length - 1]! - sorted[0]! === sorted.length - 1;

  // Additional check: all values must be integers (prevents interior non-integers from being treated as consecutive)
  const areIntegers = sorted.every(Number.isInteger);

  if (isConsecutive && areIntegers && sorted.length > 2) {
    return `${sorted[0]}-${sorted[sorted.length - 1]}`;
  }

  return sorted.join(", ");
}
